import { useState } from 'react';
import type { RecordFieldDataType, RecordFieldDef } from './recordTypes';

interface RecordFormProps {
  fields: RecordFieldDef[]; // active fields, in sort order
  defaultValues?: Record<string, string>;
  submitLabel: string;
  submitting?: boolean;
  onSubmit: (data: Record<string, string>) => void;
  onCancel: () => void;
}

const inputType: Record<RecordFieldDataType, string> = {
  Text: 'text',
  Multiline: 'text',
  Date: 'date',
  Phone: 'tel',
  Email: 'email',
  Number: 'number'
};

/** Renders a record form dynamically from the tenant's field definitions. */
export function RecordForm({ fields, defaultValues, submitLabel, submitting, onSubmit, onCancel }: RecordFormProps) {
  const [values, setValues] = useState<Record<string, string>>(() => {
    const init: Record<string, string> = {};
    for (const f of fields) init[f.key] = defaultValues?.[f.key] ?? '';
    return init;
  });
  const [error, setError] = useState<string | null>(null);

  const set = (key: string, v: string) => setValues((s) => ({ ...s, [key]: v }));

  const submit = () => {
    const missing = fields.find((f) => f.isRequired && !values[f.key]?.trim());
    if (missing) {
      setError(`${missing.label} is required.`);
      return;
    }
    setError(null);
    const data: Record<string, string> = {};
    for (const f of fields) {
      const v = values[f.key]?.trim();
      if (v) data[f.key] = v;
    }
    onSubmit(data);
  };

  return (
    <div className="space-y-3">
      <div className="grid sm:grid-cols-2 gap-3">
        {fields.map((f) => (
          <label key={f.id} className={`block ${f.dataType === 'Multiline' ? 'sm:col-span-2' : ''}`}>
            <span className="block text-sm font-medium text-slate-700 mb-1">
              {f.label}
              {f.isRequired && <span className="text-red-500"> *</span>}
            </span>
            {f.dataType === 'Multiline' ? (
              <textarea
                rows={3}
                value={values[f.key] ?? ''}
                onChange={(e) => set(f.key, e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2"
              />
            ) : (
              <input
                type={inputType[f.dataType]}
                value={values[f.key] ?? ''}
                onChange={(e) => set(f.key, e.target.value)}
                className="w-full border border-slate-300 rounded px-3 py-2 min-h-[44px]"
              />
            )}
          </label>
        ))}
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}

      <div className="flex items-center gap-3">
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="bg-sky-600 text-white px-4 py-2 rounded hover:bg-sky-700 disabled:opacity-50 text-sm"
        >
          {submitting ? 'Saving…' : submitLabel}
        </button>
        <button type="button" onClick={onCancel} className="text-slate-600 hover:text-sky-600 text-sm">
          Cancel
        </button>
      </div>
    </div>
  );
}
